import React, { useState, useMemo, useEffect } from 'react';
import AritoDataTables from '@/components/custom/arito/data-tables';
import { useSearchResults, useSelection } from './hooks';
import { getFormattedColumns } from './columns';
import { SearchTableFilterInput } from '../search-field-input';

const PAGE_SIZE = 15;
interface DataTableProps<T = any> {
  searchEndpoint?: string;
  searchColumns?: any[];
  columnDisplay?: keyof T;
  otherSearchEndpoint?: string;
  rows?: T[];
  onRowSelection?: (row: T | null) => void;
  onSelectedObjChange?: (obj: T | null) => void;
}

export const DataTable = <T = any,>({
  searchEndpoint,
  otherSearchEndpoint,
  searchColumns = [],
  columnDisplay = '' as keyof T,
  rows,
  onRowSelection,
  onSelectedObjChange
}: DataTableProps<T>) => {
  const { searchResults, isLoading, totalItems, currentPage, handlePageChange } = useSearchResults<T>(
    searchEndpoint,
    otherSearchEndpoint,
    { pageSize: 15, autoFetch: false }
  );
  const { selectedObj, selectedRowIndex, handleRowClick } = useSelection<T>();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const prevDebouncedValue = React.useRef<string | undefined>(undefined);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  React.useEffect(() => {
    onSelectedObjChange?.(selectedObj);
  }, [selectedObj, onSelectedObjChange]);

  const sourceData = rows || searchResults;

  const filteredData = useMemo(() => {
    if (searchEndpoint || otherSearchEndpoint) {
      return sourceData;
    }

    if (!debouncedSearchValue.trim()) {
      return sourceData;
    }

    const searchTerm = debouncedSearchValue.toLowerCase().trim();

    const filtered = sourceData.filter((row: any) => {
      const matches = Object.values(row).some((value: any) => {
        if (value === null || value === undefined) return false;

        const stringValue = String(value).toLowerCase();
        return stringValue.includes(searchTerm);
      });
      return matches;
    });

    return filtered;
  }, [sourceData, debouncedSearchValue, searchEndpoint, otherSearchEndpoint]);

  useEffect(() => {
    if (!searchEndpoint && !otherSearchEndpoint) return;
    if (prevDebouncedValue.current === debouncedSearchValue) return;

    prevDebouncedValue.current = debouncedSearchValue;
    handlePageChange(0, debouncedSearchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const pageSelectionResetKey = `${currentPage}-${debouncedSearchValue}-${searchEndpoint || otherSearchEndpoint || ''}`;
  const tableData = [
    {
      name: '',
      rows: filteredData,
      columns: getFormattedColumns(searchColumns, {
        currentPageRows: filteredData,
        pageSelectionResetKey
      })
    }
  ];

  return (
    <>
      <div className='flex items-center border-b border-gray-200 px-4 py-2'>
        <SearchTableFilterInput value={searchValue} onChange={e => setSearchValue(e.target.value)} />
      </div>

      <div className='flex size-full min-h-[600px] flex-col border border-[#eaeaea] bg-white'>
        {isLoading && (
          <div className='flex h-64 items-center justify-center'>
            <div className='h-8 w-8 animate-spin rounded-full border-2 border-[rgba(15,118,110,0.9)] border-t-transparent'></div>
          </div>
        )}

        {!isLoading && (
          <AritoDataTables
            tables={tableData}
            onRowClick={(params: any) => {
              handleRowClick(params);
              onRowSelection?.(params.row);
            }}
            selectedRowId={selectedRowIndex || undefined}
            getRowId={row => row.uuid || String(row[columnDisplay])}
            pageSize={PAGE_SIZE}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={page => handlePageChange(page, debouncedSearchValue)}
            serverSidePagination={true}
          />
        )}
      </div>
    </>
  );
};
